import { useTranslation } from '../../../i18n';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Empty, InputNumber, Modal, Select, Slider, Space, Spin, Tag, Typography } from 'antd';
import Money from '../../Money';
import { formatMoney, PLATFORM_CURRENCY } from '../../../utils/money';
import { errMsg, ok, runAction } from './utils';

const { Text } = Typography;

const fmt = (v) => String(+Number(v || 0).toFixed(2));

/** Per-VM allowance, e.g. "最多 2 个 · 已用 1" (red at the cap). */
const Allowance = ({ max, used, unit }) => {
    const { t } = useTranslation();
    if (max === undefined || max === null) return null;
    return <Tag color={used >= max ? 'red' : undefined}>{t('Max {{max}} {{unit}} · used {{used}}', { max, used, unit })}</Tag>;
};

/** One resource: slider/number constrained by the node config + live quote + confirm. */
const ResourceRow = ({ id, res, adapter, disabled, isMobile, onDone, onInvoice }) => {
    const { t } = useTranslation();
    const isDataDisk = res.resource === 'data_disk';
    const disks = res.disks || [];
    const [diskId, setDiskId] = useState(0); // 0 = new data disk
    const disk = disks.find((d) => d.id === diskId);
    const current = isDataDisk ? (disk ? disk.size : 0) : Number(res.current);
    const isDisk = isDataDisk || res.resource === 'sys_disk';
    // disks only grow: the lower bound is the current size
    const min = isDisk && current > res.min ? Math.ceil((current - res.min) / res.step) * res.step + res.min : res.min;
    // data disks: per-VM count / total GB caps
    const hasCaps = isDataDisk && res.max_count !== undefined;
    const gbLeft = hasCaps ? Math.max(0, res.max_total_gb - res.used_total_gb) : Infinity;
    const max = isDataDisk ? Math.min(res.max, current + gbLeft) : res.max;
    const capped = hasCaps && ((!disk && res.used_count >= res.max_count) || gbLeft <= 0 || max < min);
    const [target, setTarget] = useState(null);
    const [quote, setQuote] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => { setTarget(null); setQuote(null); }, [diskId]);

    useEffect(() => {
        if (target === null || target === undefined || Number(target) === current) { setQuote(null); return undefined; }
        const h = setTimeout(async () => {
            const params = { resource: res.resource, target };
            if (disk) params.disk_id = disk.id;
            const r = await adapter.quoteElastic(id, params);
            setQuote(ok(r) ? r.data : { error: errMsg(r, t('Unable to quote')) });
        }, 350);
        return () => clearTimeout(h);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target, diskId]);

    const submit = () => {
        const down = quote?.downgrade;
        const lines = [];
        if (isDisk) {
            lines.push(<Text key="d" type="danger">{t('Disks can\'t be shrunk after expansion')}</Text>);
            lines.push(<Text key="g" type="secondary">{t('Expanded online; the partition inside the OS must be grown (done automatically on restart)')}</Text>);
        } else {
            lines.push(<Text key="r" type="warning">{t('Takes effect after the instance restarts')}</Text>);
        }
        if (down) lines.push(<Text key="n" type="secondary">{t('Downgrades aren\'t refunded; the new configuration is billed from the next cycle')}</Text>);
        Modal.confirm({
            title: down ? t('Confirm downgrade?') : t('Confirm payment of {{v}}?', { v: formatMoney(fmt(quote.amount), quote.currency || PLATFORM_CURRENCY) }),
            content: <Space direction="vertical" size={4}>{lines}</Space>,
            okText: down ? t('Confirm') : t('Pay'),
            cancelText: t('Cancel'),
            onOk: async () => {
                setBusy(true);
                const data = { resource: res.resource, target };
                if (disk) data.disk_id = disk.id;
                const r = await runAction(() => adapter.buyElastic(id, data), t('Submitted'), t('Operation failed'));
                setBusy(false);
                if (!r) return;
                if (r.data?.invoice_id && !r.data?.paid) onInvoice(r.data.invoice_id);
                else onDone();
            },
        });
    };

    return (
        <div style={{ padding: '12px 0', borderBottom: '1px solid rgba(128,128,128,0.15)' }}>
            <Space wrap size={8} style={{ marginBottom: 8 }}>
                <Text strong>{t(res.label)}</Text>
                {(!isDataDisk || disk) && <Text type="secondary">{t('Current')} {fmt(current)} {res.unit}</Text>}
                {res.restart ? <Tag>{t('Restart required')}</Tag> : null}
                {hasCaps ? <Allowance max={res.max_count} used={res.used_count} unit={t('items')} /> : null}
                {hasCaps ? <Allowance max={res.max_total_gb} used={res.used_total_gb} unit="GB" /> : null}
                <Text type="secondary" style={{ fontSize: 12 }}><Money amount={res.unit_price} currency={res.currency || PLATFORM_CURRENCY} /> / {res.unit}·{res.period_months > 1 ? t('{{n}} months', { n: res.period_months }) : t('Month')}</Text>
            </Space>
            {isDataDisk && (
                <div style={{ marginBottom: 8 }}>
                    <Select
                        size="small"
                        style={{ width: isMobile ? '100%' : 220 }}
                        value={diskId}
                        onChange={setDiskId}
                        options={[{ value: 0, label: t('Add data disk') }, ...disks.map((d) => ({ value: d.id, label: `${t('Expand capacity')} ${d.dev} (${d.size} GB)` }))]}
                    />
                </div>
            )}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                {!capped && max > min && (
                    <Slider
                        style={{ flex: 1, minWidth: 160 }}
                        min={min}
                        max={max}
                        step={res.step}
                        value={target ?? Math.max(min, Math.min(max, current))}
                        onChange={setTarget}
                        disabled={disabled}
                    />
                )}
                <InputNumber
                    min={min}
                    max={max}
                    step={res.step}
                    value={target ?? Math.max(min, Math.min(max, current))}
                    onChange={setTarget}
                    addonAfter={res.unit}
                    disabled={disabled || capped}
                    style={{ width: 150 }}
                />
                <div style={{ minWidth: 120 }}>
                    {quote?.error ? <Text type="danger" style={{ fontSize: 12 }}>{quote.error}</Text> : null}
                    {quote && !quote.error ? (quote.downgrade
                        ? <Text type="secondary">{t('Downgrade · Free')}</Text>
                        : <Text strong><Money amount={quote.amount} currency={quote.currency} /> <Text type="secondary" style={{ fontSize: 12 }}>{t('{{d}} days', { d: quote.days })}</Text></Text>) : null}
                </div>
                <Button type="primary" disabled={disabled || capped || !quote || !!quote.error} loading={busy} onClick={submit}>
                    {quote?.downgrade ? t('Downgrade') : t('Upgrade')}
                </Button>
            </div>
        </div>
    );
};

const SOURCE = { plan: 'Plan', order: 'Purchased', addon: 'Purchased', other: '' };

/** IPv6 blocks: list (plan ones fixed), buy one more size, release a bought one. */
const Ipv6Row = ({ id, res, adapter, disabled, onDone, onInvoice }) => {
    const { t } = useTranslation();
    const [len, setLen] = useState(null);
    const [busy, setBusy] = useState(false);
    const blocks = res.blocks || [];
    const sizes = (res.sizes || []).filter((s) => s.available);
    const canBuy = sizes.length > 0 && res.bought < res.max_blocks;

    const buy = async () => {
        const q = await adapter.quoteElastic(id, { resource: 'ipv6_block', target: len });
        if (!ok(q)) { Modal.error({ title: errMsg(q, t('Unable to quote')) }); return; }
        Modal.confirm({
            title: t('Confirm payment of {{v}}?', { v: formatMoney(fmt(q.data.amount), q.data.currency || PLATFORM_CURRENCY) }),
            content: <Text type="warning">{t('Takes effect after the instance restarts')}</Text>,
            okText: t('Pay'),
            cancelText: t('Cancel'),
            onOk: async () => {
                setBusy(true);
                const r = await runAction(() => adapter.buyElastic(id, { resource: 'ipv6_block', target: len }), t('Submitted'), t('Operation failed'));
                setBusy(false);
                if (!r) return;
                if (r.data?.invoice_id && !r.data?.paid) onInvoice(r.data.invoice_id);
                else onDone();
            },
        });
    };

    const release = (b) => Modal.confirm({
        title: t('Release {{p}}?', { p: b.prefix }),
        content: <Text type="secondary">{t('The address will be reclaimed with no refund; takes effect after restart')}</Text>,
        okButtonProps: { danger: true },
        okText: t('Release'),
        cancelText: t('Cancel'),
        onOk: async () => {
            const r = await runAction(() => adapter.releaseIpv6(id, b.id), t('Released'), t('Operation failed'));
            if (r) onDone();
        },
    });

    return (
        <div style={{ padding: '12px 0', borderBottom: '1px solid rgba(128,128,128,0.15)' }}>
            <Space wrap size={8} style={{ marginBottom: blocks.length ? 8 : 0 }}>
                <Text strong>IPv6</Text>
                <Tag>{t('Restart required')}</Tag>
                {sizes.length > 0 ? <Allowance max={res.max_blocks} used={res.bought} unit={t('items')} /> : null}
            </Space>
            {blocks.map((b) => (
                <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                    <Space wrap size={6}>
                        <Text code copyable>{b.prefix}</Text>
                        {b.address ? <Text type="secondary" style={{ fontSize: 12 }}>{b.address}</Text> : null}
                        {SOURCE[b.source] ? <Tag>{t(SOURCE[b.source])}</Tag> : null}
                    </Space>
                    {b.releasable ? <Button size="small" danger disabled={disabled} onClick={() => release(b)}>{t('Release')}</Button> : null}
                </div>
            ))}
            {canBuy && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
                    <Select
                        size="small"
                        style={{ minWidth: 200 }}
                        placeholder={t('Choose a spec')}
                        value={len}
                        onChange={setLen}
                        options={sizes.map((s) => ({
                            value: s.len,
                            label: <span>/{s.len} · <Money amount={s.price / (s.period_months || 1)} currency={s.currency || PLATFORM_CURRENCY} />{t('/month')}</span>,
                        }))}
                    />
                    <Button type="primary" size="small" loading={busy} disabled={disabled || !len} onClick={buy}>{t('Buy')}</Button>
                </div>
            )}
        </div>
    );
};

/** Extra public IPv4 of the VM's node (bound to this VM once paid). */
const Ipv4Row = ({ id, res, adapter, disabled, onDone, onInvoice }) => {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const atCap = res.max_per_vm !== undefined && res.used >= res.max_per_vm;
    const buy = () => Modal.confirm({
        title: res.unit_price > 0 ? t('Confirm payment of {{v}}?', { v: formatMoney(fmt(res.amount ?? res.unit_price / (res.period_months || 1)), res.currency || PLATFORM_CURRENCY) }) : t('Confirm purchase?'),
        content: <Text type="secondary">{res.days ? t('{{n}} days (until the instance expires), bound to this instance automatically', { n: res.days }) : t('30 days, bound to this instance automatically')}</Text>,
        okText: t('Pay'),
        cancelText: t('Cancel'),
        onOk: async () => {
            setBusy(true);
            const r = await runAction(() => adapter.buyEip(id), t('Submitted'), t('Operation failed'));
            setBusy(false);
            if (!r) return;
            if (r.data?.invoice_id && !r.data?.paid) onInvoice(r.data.invoice_id);
            else onDone();
        },
    });
    return (
        <div style={{ padding: '12px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <Space wrap size={8}>
                <Text strong>{t('Extra IPv4')}</Text>
                <Allowance max={res.max_per_vm} used={res.used} unit={t('items')} />
                <Text type="secondary" style={{ fontSize: 12 }}><Money amount={res.unit_price / (res.period_months || 1)} currency={res.currency || PLATFORM_CURRENCY} /> / {t('Month')}</Text>
            </Space>
            <Button type="primary" loading={busy} disabled={disabled || !res.available || atCap} onClick={buy}>{atCap ? t('Limit reached') : res.available ? t('Buy') : t('No addresses available')}</Button>
        </div>
    );
};

const ROWS = { ipv6: Ipv6Row, ipv4: Ipv4Row };

/** User VM detail → 升级/扩容: only the resources the VM's node sells. */
const UpgradeTab = ({ id, adapter, isMobile, disabled, reload, active, onInvoice }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const r = await adapter.getElastic(id);
        setData(ok(r) ? r.data : { resources: [] });
        setLoading(false);
    }, [adapter, id]);

    useEffect(() => { if (active) load(); }, [active, load]);

    const done = () => { load(); reload(); };

    if (loading && !data) return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;
    const list = data?.resources || [];
    if (!list.length) return <Empty description={t('Not yet available')} />;

    return (
        <Card size="small" bodyStyle={{ paddingTop: 0, paddingBottom: 0 }}>
            {list.map((res) => {
                const Row = ROWS[res.resource];
                return Row
                    ? <Row key={res.resource} id={id} res={res} adapter={adapter} disabled={disabled} onDone={done} onInvoice={onInvoice} />
                    : <ResourceRow key={res.resource} id={id} res={res} adapter={adapter} disabled={disabled} isMobile={isMobile} onDone={done} onInvoice={onInvoice} />;
            })}
        </Card>
    );
};

export default UpgradeTab;
